"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, MapPin, CircleCheck as CheckCircle2, ArrowRight, Search, Quote, Building2, Calendar, ChartBar as BarChart3, ExternalLink } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const caseStudiesData = [{
  id: "dental-clinic",
  title: "Regional Healthcare Practice: +184% Organic Inquiries",
  category: "Healthcare & Medical",
  location: "Swindon & Wiltshire",
  metricHighlight: "+184%",
  metricLabel: "Inbound Phone Calls & Bookings",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_dental.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_dental.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_dental.png-wvc-sizes",
  challenge: "The clinic was heavily reliant on expensive PPC ads (£3,200/month) and struggled to rank in the local 3-pack for high-intent dental search queries in Wiltshire.",
  strategy: "I audited their local search footprint, restructured geo-targeted service pages, and built a sustainable Google Business Profile optimization framework.",
  implementation: "Implemented localized schema markup, optimized NAP consistency, built hyper-local citations, and instituted a review acquisition workflow for past patients.",
  results: ["+184% increase in organic phone inquiries and contact forms", "#1 Google Maps Local Pack placement for 14 primary keywords", "Top 3 organic rankings for high-value cosmetic dental terms"],
  impact: "Reduced Google Ads monthly spend by £2,400 while doubling new consultation bookings within 6 months.",
  testimonial: {
    quote: "Working directly with Yassine transformed our practice. He delivered exactly what was promised with complete transparency.",
    author: "Dr. M. Patel",
    role: "Lead Practice Director"
  }
}, {
  id: "home-showroom",
  title: "Boutique Home Showroom: 3x Organic Showroom Footfall",
  category: "Retail & Showroom",
  location: "Oxfordshire & Bristol",
  metricHighlight: "3.2x",
  metricLabel: "Qualified Local Leads",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_showroom.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_showroom.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_showroom.png-wvc-sizes",
  challenge: "National chain competitors were outranking this independent showroom for local high-value interior design and bespoke kitchen searches.",
  strategy: "I engineered a localized content hierarchy targeting commercial search intent and resolved core technical crawling issues that suppressed local landing pages.",
  implementation: "Created regional hub pages, optimized technical image SEO for showroom portfolios, and acquired authoritative local editorial links.",
  results: ["+210% surge in organic local search impressions", "#1 ranking for high-intent regional showroom queries", "320+ verified showroom visit requests in Q3 alone"],
  impact: "Drove £140,000+ in attributed showroom sales directly from organic search traffic over a 9-month campaign.",
  testimonial: {
    quote: "No agency fluff—just direct expert guidance from Yassine that yielded real footfall and genuine high-ticket orders.",
    author: "Sarah Jenkins",
    role: "Commercial Director"
  }
}, {
  id: "b2b-logistics",
  title: "Commercial Logistics Provider: +145% B2B Qualified Leads",
  category: "B2B & Commercial Services",
  location: "South West UK",
  metricHighlight: "+145%",
  metricLabel: "Enterprise Inquiries",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_logistics.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_logistics.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_logistics.png-wvc-sizes",
  challenge: "An outdated site structure and poor local SEO technical foundation left this logistics company invisible to regional enterprise procurement managers.",
  strategy: "I led a comprehensive technical site migration, mapped geo-targeted service offerings, and executed a targeted B2B link acquisition drive.",
  implementation: "Cleaned JavaScript rendering issues, optimized core web vitals, structured B2B landing pages, and published industry-specific research content.",
  results: ["+145% increase in high-value contract inquiry forms", "Page 1 rankings across 28 regional commercial logistics terms", "4.8x boost in landing page conversion efficiency"],
  impact: "Secured 12 major long-term corporate logistics contracts directly traceable to organic search landing pages.",
  testimonial: {
    quote: "Yassine's hands-on approach and direct access meant rapid execution. The ROI speaks for itself.",
    author: "David Ross",
    role: "Operations Head"
  }
}];
export default function Localimpact() {
  const [activeTab, setActiveTab] = useState("dental-clinic");
  const currentStudy = caseStudiesData.find(study => study.id === activeTab) || caseStudiesData[0];
  return <section id="localimpactcasestudies" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <Badge variant="outline" className="mb-4 border-primary/20 bg-muted text-primary px-3 py-1 text-xs uppercase tracking-wider font-semibold">
            Verified SEO Outcomes
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default">
            Local Impact Case Studies
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Real outcomes from my direct SEO consultancy. I work hands-on with UK local businesses to build organic visibility, outrank national competitors, and capture high-intent customers.
          </p>
        </div>

        {/* Tab Selector */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
          {caseStudiesData.map((study, i) => {
          const isActive = study.id === activeTab;
          return <Button key={study.id} data-index={i} variant={isActive ? "default" : "outline"} onClick={() => setActiveTab(study.id)} className={`transition-colors duration-200 text-xs sm:text-sm font-medium ${isActive ? "bg-primary text-primary-foreground shadow-sm" : "bg-background text-foreground hover:bg-muted"}`}>
                <Building2 className="w-4 h-4 mr-2" />
                {study.category}
              </Button>;
        })}
        </div>

        {/* Active Case Study Details */}
        <motion.div key={currentStudy.id} initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        ease: "easeOut"
      }} className="rounded-2xl border border-border bg-card shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* Left Column: Key Stats, Image & Testimonial */}
            <div className="lg:col-span-5 bg-muted/40 p-6 sm:p-8 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-border">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground mb-3">
                  <MapPin className="w-3.5 h-3.5 text-accent" />
                  <span>{currentStudy.location}</span>
                  <span className="text-border">•</span>
                  <span>{currentStudy.category}</span>
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-card-foreground font-default mb-4 leading-tight">
                  {currentStudy.title}
                </h3>

                {/* Metric Highlight Box */}
                <div className="bg-background rounded-xl p-5 border border-border shadow-xs my-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl sm:text-5xl font-extrabold text-primary tracking-tight">
                      {currentStudy.metricHighlight}
                    </span>
                    <TrendingUp className="w-6 h-6 text-accent" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground mt-1">
                    {currentStudy.metricLabel}
                  </p>
                </div>

                {/* Visual Media */}
                <div className="relative rounded-xl overflow-hidden border border-border shadow-xs mb-6">
                  <img src={currentStudy.image} data-wvc-srcset={currentStudy.imageSrcset} data-wvc-sizes={currentStudy.imageSizes} alt={currentStudy.title} loading="lazy" className="w-full h-48 sm:h-56 object-cover" />
                  <div className="absolute inset-0 bg-primary/10" />
                </div>
              </div>

              {/* Direct Client Quote */}
              <div className="bg-secondary/40 rounded-xl p-4 border border-secondary/60 text-secondary-foreground">
                <Quote className="w-5 h-5 text-accent mb-2" />
                <p className="text-xs sm:text-sm italic leading-relaxed font-default">
                  "{currentStudy.testimonial.quote}"
                </p>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="font-semibold">{currentStudy.testimonial.author}</span>
                  <span className="text-muted-foreground">{currentStudy.testimonial.role}</span>
                </div>
              </div>
            </div>

            {/* Right Column: Breakdown Narrative */}
            <div className="lg:col-span-7 p-6 sm:p-8 lg:p-10 flex flex-col justify-between">
              <div className="space-y-6">
                {/* Challenge */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent mb-1.5">
                    <span className="w-2 h-2 rounded-full bg-accent inline-block" />
                    Challenge
                  </div>
                  <p className="text-sm sm:text-base text-card-foreground/90 leading-relaxed font-default">
                    {currentStudy.challenge}
                  </p>
                </div>

                <Separator />

                {/* Strategy */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary mb-1.5">
                    <span className="w-2 h-2 rounded-full bg-primary inline-block" />
                    Strategy
                  </div>
                  <p className="text-sm sm:text-base text-card-foreground/90 leading-relaxed font-default">
                    {currentStudy.strategy}
                  </p>
                </div>

                <Separator />

                {/* Implementation */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-chart-4 mb-1.5">
                    <span className="w-2 h-2 rounded-full bg-chart-4 inline-block" />
                    Implementation
                  </div>
                  <p className="text-sm sm:text-base text-card-foreground/90 leading-relaxed font-default">
                    {currentStudy.implementation}
                  </p>
                </div>

                <Separator />

                {/* Results */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-foreground mb-3">
                    <BarChart3 className="w-4 h-4 text-primary" />
                    Verified Results
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {currentStudy.results.map((res, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2 bg-muted/30 p-2.5 rounded-lg border border-border/50 text-xs sm:text-sm font-medium">
                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <span>{res}</span>
                      </li>)}
                  </ul>
                </div>

                <Separator />

                {/* Business Impact Summary */}
                <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
                  <div className="text-xs font-bold uppercase tracking-wider text-primary mb-1">
                    Business Impact
                  </div>
                  <p className="text-sm sm:text-base font-semibold text-foreground">
                    {currentStudy.impact}
                  </p>
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row gap-3 items-center justify-between">
                <Link to="/CaseStudiesPage" className="w-full sm:w-auto">
                  <Button variant="outline" size="default" className="w-full sm:w-auto text-xs sm:text-sm">
                    View All Case Studies
                    <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </Link>
                <Link to="/ContactPage" className="w-full sm:w-auto">
                  <Button variant="default" size="default" className="w-full sm:w-auto text-xs sm:text-sm font-semibold">
                    Book a Free SEO Consultation
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Reassurance Bar */}
        <div className="mt-16 bg-muted/50 border border-border rounded-xl p-6 sm:p-8 text-center max-w-4xl mx-auto">
          <h3 className="text-lg sm:text-xl font-bold text-foreground font-default mb-2">
            No Fabricated Metrics. Direct Consultant Access.
          </h3>
          <p className="text-sm text-muted-foreground font-default max-w-2xl mx-auto mb-6">
            When you hire YE Consultancy, you work directly with me—Yassine Essaadi. I don't hand off projects to junior account managers or fabricate SEO client statistics.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 text-xs sm:text-sm text-foreground font-medium">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              Tailored Strategy
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              Transparent Monthly Reporting
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary" />
              100% Honest Measurement
            </span>
          </div>
        </div>
      </div>
    </section>;
}